/* Utilities to fully preload site assets (fonts + images) in the browser.
   - Designed for a full-blocking preload that runs while the site's loader is shown.
*/


function extractFromSrcset(srcset) {
    if (!srcset) return [];
    return srcset.split(',').map(s => s.trim().split(' ')[0]).filter(Boolean);
}

function crawlDOMForImages() {
    if (typeof document === 'undefined') return [];
    const urls = new Set();
    const imgs = Array.from(document.querySelectorAll('img'));
    for (const img of imgs) {
        if (img.src && !img.src.includes('data:')) urls.add(img.src);
        const srcset = img.getAttribute('srcset');
        extractFromSrcset(srcset).forEach(u => urls.add(u));
    }
    const sources = Array.from(document.querySelectorAll('source'));
    for (const s of sources) {
        const src = s.src || s.getAttribute('src');
        if (src && !src.includes('data:')) urls.add(src);
        const srcset = s.getAttribute('srcset');
        extractFromSrcset(srcset).forEach(u => urls.add(u));
    }
    return Array.from(urls).map(u => (u.startsWith(window.location.origin) ? u.replace(window.location.origin, '') : u));
}

function preloadImage(url, signal) {
    return new Promise((resolve, reject) => {
        const img = new Image();
        if (signal) {
            if (signal.aborted) return reject(new DOMException('aborted', 'AbortError'));
            signal.addEventListener('abort', () => {
                img.src = '';
                reject(new DOMException('aborted', 'AbortError'));
            }, { once: true });
        }
        img.onload = () => resolve();
        img.onerror = () => reject(new Error('failed to load ' + url));
        img.src = url;
    });
}

async function warmGoogleFonts() {
    if (typeof document === 'undefined') return;
    try {
        const outfits = [300, 400, 500, 600, 700, 800];
        const sync = [400, 700];
        const fns = [];
        for (const w of outfits) fns.push(document.fonts.load(`${w} 1em Outfit`));
        for (const w of sync) fns.push(document.fonts.load(`${w} 1em Syncopate`));

        // Also fetch the CSS to be sure
        const href = 'https://fonts.googleapis.com/css2?family=Syncopate:wght@400;700&family=Outfit:wght@300;400;500;600;700;800&display=swap';
        await fetch(href, { cache: 'force-cache' });

        await Promise.allSettled(fns);
    } catch (err) {
        // ignore
    }
}

export async function preloadAll(opts) {
    const onProgress = opts?.onProgress;
    const timeoutMs = typeof opts?.timeoutMs === 'number' ? opts.timeoutMs : 60000;
    const controller = new AbortController();
    const combinedSignal = opts?.signal ?? controller.signal;

    // No sprite sheets to preload (sprite system removed)

    // 2. DOM crawl (captures logos, icons outside sprites)
    const domUrls = crawlDOMForImages();

    // combine + dedupe
    const all = new Set(domUrls);

    const urls = Array.from(all)
        .map(u => u.split('?')[0])
        .filter(u => !u.startsWith('data:'))
        .map(u => (u.startsWith('http') ? u : u.startsWith('/') ? u : `/${u}`));

    // fonts: warm in parallel
    const fontPromise = warmGoogleFonts();

    const total = Math.max(1, urls.length + 1);
    let loaded = 0;
    onProgress?.({ loaded, total });

    const CONCURRENCY = 6;
    const queue = urls.slice();
    const errors = [];

    const workers = [];
    for (let i = 0; i < CONCURRENCY; i++) {
        const worker = (async () => {
            while (queue.length) {
                const url = queue.shift();
                try {
                    await preloadImage(url, combinedSignal);
                    loaded += 1;
                    onProgress?.({ loaded, total, url });
                } catch (err) {
                    errors.push(String(err?.message || err));
                    loaded += 1;
                    onProgress?.({ loaded, total, url });
                }
            }
        })();
        workers.push(worker);
    }

    const allPromise = Promise.allSettled([Promise.all(workers), fontPromise]).then(() => ({ errors }));
    const timeoutPromise = new Promise((resolve) => setTimeout(() => resolve({ errors: [...errors, 'timeout'] }), timeoutMs));

    const result = await Promise.race([allPromise, timeoutPromise]);
    onProgress?.({ loaded: total, total });

    return {
        total,
        loaded,
        errors: result.errors,
        timedOut: result.errors.includes('timeout')
    };
}
